import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { TicketStatus } from "../../../store/slices/ticketingPortalSlice";
import { KanbanFilters } from "../hooks/useSustainQueueListing";

// ── Types ─────────────────────────────────────────────────────────────────────

type FilterOptions = {
  categories: string[];
  subcategories: string[];
  locations: string[];
  markets: string[];
  regions: string[];
  tertiaries: string[];
};

type ManagerOption = { email: string; label: string };

type Props = {
  query: string;
  filters: KanbanFilters;
  assignableUsers: string[];
  filterOptions: FilterOptions;
  managerOptions: ManagerOption[];
  resultCount?: number;
  onQueryChange: (q: string) => void;
  onFiltersChange: (filters: KanbanFilters) => void;
  isDark?: boolean;
};

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { key: TicketStatus; label: string }[] = [
  { key: "new", label: "New" },
  { key: "progress", label: "In Progress" },
  { key: "waiting-it", label: "Escalated to IT" },
  { key: "waiting-tmv", label: "Waiting on Vendor" },
  { key: "waiting-vendor", label: "Waiting on Vendor" },
  { key: "submitted-ado", label: "Submitted to ADO" },
  { key: "waiting-user", label: "Waiting on User" },
  { key: "resolved", label: "Closed" },
];

const URGENCY_OPTIONS = ["critical", "high", "medium", "low", "informational"];
const URGENCY_LABELS: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  informational: "Informational",
};

// ── Multi-select dropdown ─────────────────────────────────────────────────────

type MultiSelectProps = {
  label: string;
  options: string[];
  optionLabels?: Record<string, string>;
  selected: string[];
  onChange: (next: string[]) => void;
  isDark?: boolean;
};

const MultiSelectDropdown: React.FC<MultiSelectProps> = ({
  label,
  options,
  optionLabels,
  selected,
  onChange,
  isDark,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<View>(null);
  const safeLabel = label.replace(/\s/g, "-");
  const triggerId = `sq-filter-trigger-${safeLabel}`;
  const menuId = `sq-filter-menu-${safeLabel}`;

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );

  const clearAll = () => onChange([]);

  const reposition = useCallback(() => {
    if (Platform.OS !== "web") return;
    const el = document.getElementById(triggerId);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(200, rect.width) });
    }
  }, [triggerId]);

  const openMenu = () => {
    reposition();
    setOpen((v) => !v);
  };

  // Close on outside click and reposition on scroll/resize
  useEffect(() => {
    if (!open || Platform.OS !== "web") return;
    const handleClick = (e: MouseEvent) => {
      const menu = document.getElementById(menuId);
      const trigger = document.getElementById(triggerId);
      const target = e.target as Node;
      if (
        (!trigger || !trigger.contains(target)) &&
        (!menu || !menu.contains(target))
      ) setOpen(false);
    };
    const handleRepos = () => reposition();
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("resize", handleRepos);
    window.addEventListener("scroll", handleRepos, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("resize", handleRepos);
      window.removeEventListener("scroll", handleRepos, true);
    };
  }, [open, menuId, triggerId, reposition]);

  const displayLabel =
    selected.length === 0
      ? label
      : selected.length === 1
      ? (optionLabels?.[selected[0]] ?? selected[0])
      : `${label} (${selected.length})`;

  // Portal menu — rendered directly into document.body so it escapes all parent overflow/clipping
  const portalMenu =
    open && Platform.OS === "web"
      ? createPortal(
          <div
            id={menuId}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              minWidth: pos.width,
              background: isDark ? "#2B2B2B" : "#ffffff",
              borderRadius: 8,
              border: isDark ? "1px solid #404040" : "1px solid #e5e7eb",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 99999,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ maxHeight: 170, overflowY: "auto" }}>
              {options.map((opt) => {
                const isSel = selected.includes(opt);
                const displayOpt = optionLabels?.[opt] ?? opt;
                return (
                  <div
                    key={opt}
                    onMouseDown={(e) => { e.preventDefault(); toggle(opt); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                      background: isSel ? (isDark ? "#1a2f4a" : "#eff6ff") : "transparent",
                      color: isSel ? "#3D8BFF" : (isDark ? "#F2F2F2" : "#1f2937"),
                      fontSize: 13,
                      fontFamily: "inherit",
                      userSelect: "none",
                    }}
                  >
                    <div style={{
                      width: 14, height: 14, borderRadius: 3,
                      border: isSel ? "none" : `1.5px solid ${isDark ? "#555" : "#d1d5db"}`,
                      background: isSel ? "#005CBC" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {isSel && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    {displayOpt}
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div
                onMouseDown={(e) => { e.preventDefault(); clearAll(); }}
                style={{
                  padding: "8px 12px",
                  borderTop: isDark ? "1px solid #404040" : "1px solid #f3f4f6",
                  color: isDark ? "#3D8BFF" : "#005CBC",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  textAlign: "right",
                  userSelect: "none",
                  background: isDark ? "#222222" : "#f9fafb",
                }}
              >
                Clear all
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  // Fallback native menu for iOS/Android
  const nativeMenu = open && Platform.OS !== "web" ? (
    <View style={[s.dropMenu, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}>
        <ScrollView style={{ maxHeight: 160 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        {options.map((opt) => {
          const isSel = selected.includes(opt);
          const displayOpt = optionLabels?.[opt] ?? opt;
          return (
            <Pressable
              key={opt}
              style={({ pressed }) => [s.dropItem, isSel && (isDark ? s.dropItemActiveDark : s.dropItemActive), pressed && s.dropItemPressed]}
              onPress={() => toggle(opt)}
            >
              <View style={[s.checkbox, isSel && s.checkboxActive]}>
                {isSel && <MaterialIcons name="check" size={11} color="#ffffff" />}
              </View>
              <Text style={[s.dropItemText, isDark && { color: "#F2F2F2" }, isSel && s.dropItemTextActive]}>{displayOpt}</Text>
            </Pressable>
          );
        })}
      </ScrollView>
      {selected.length > 0 && (
        <Pressable style={[s.clearAllBtn, isDark && { borderTopColor: "#404040" }]} onPress={clearAll}>
          <Text style={[s.clearAllText, isDark && { color: "#B0B0B0" }]}>Clear all</Text>
        </Pressable>
      )}
    </View>
  ) : null;

  return (
    <View style={s.dropWrap}>
      <Pressable
        ref={triggerRef}
        // @ts-ignore web id
        id={triggerId}
        nativeID={triggerId}
        style={[s.dropTrigger, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, selected.length > 0 && s.dropTriggerActive, selected.length > 0 && isDark && { backgroundColor: "#1a2f4a", borderColor: "#3D8BFF" }]}
        onPress={openMenu}
      >
        <Text
          style={[s.dropTriggerText, isDark && { color: "#B0B0B0" }, selected.length > 0 && s.dropTriggerTextActive]}
          numberOfLines={1}
        >
          {displayLabel}
        </Text>
        {selected.length > 0 ? (
          <Pressable
            onPress={(e) => { (e as any).stopPropagation?.(); clearAll(); }}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          >
            <MaterialIcons name="close" size={13} color="#3D8BFF" />
          </Pressable>
        ) : (
          <MaterialIcons
            name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={16}
            color={isDark ? "#B0B0B0" : "#585858"}
          />
        )}
      </Pressable>
      {nativeMenu}
      {portalMenu}
    </View>
  );
};

// ── Filter Bar ────────────────────────────────────────────────────────────────

const SustainQueueFilterBar: React.FC<Props> = ({
  query,
  filters,
  assignableUsers,
  filterOptions,
  managerOptions,
  resultCount,
  onQueryChange,
  onFiltersChange,
  isDark,
}) => {
  const set = useCallback(
    <K extends keyof KanbanFilters>(key: K, value: KanbanFilters[K]) =>
      onFiltersChange({ ...filters, [key]: value }),
    [filters, onFiltersChange]
  );

  const activeCount = Object.values(filters).flat().length + (query.trim() ? 1 : 0);

  const clearAll = () => {
    onQueryChange("");
    onFiltersChange({
      statuses: [],
      urgencies: [],
      categories: [],
      subcategories: [],
      locations: [],
      markets: [],
      regions: [],
      assignees: [],
      tertiaries: [],
      managers: [],
    });
  };

  return (
    <View style={[s.root, isDark && { backgroundColor: "#202020", borderColor: "#404040" }]}>
      {/* ── Search toolbar (matches Table view) ── */}
      <View style={s.searchToolbar}>
        <View style={s.searchWrap}>
          <MaterialIcons name="search" size={14} color={isDark ? "#6E6E6E" : "#818181"} style={s.searchIcon} />
          <TextInput
            style={[s.searchInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" } as any]}
            value={query}
            onChangeText={onQueryChange}
            placeholder="Search Ticket ID, Title, Description, Submitter"
            placeholderTextColor={isDark ? "#6E6E6E" : "#9099aa"}
          />
          {query.length > 0 && (
            <Pressable onPress={() => onQueryChange("")} hitSlop={8} style={s.searchClearBtn}>
              <MaterialIcons name="close" size={13} color={isDark ? "#6E6E6E" : "#818181"} />
            </Pressable>
          )}
        </View>
        {resultCount !== undefined && (
          <Text style={[s.resultCount, isDark && { color: "#B0B0B0" }]}>{resultCount} tickets</Text>
        )}
      </View>

      {/* Divider */}
      <View style={[s.divider, isDark && { backgroundColor: "#363636" }]} />

      {/* Filter dropdowns */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={s.filtersRow}
        keyboardShouldPersistTaps="handled"
      >
        <MultiSelectDropdown
          label="Status"
          options={STATUS_OPTIONS.map((s) => s.key)}
          optionLabels={Object.fromEntries(STATUS_OPTIONS.map((s) => [s.key, s.label]))}
          selected={filters.statuses}
          onChange={(v) => set("statuses", v as TicketStatus[])}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Urgency"
          options={URGENCY_OPTIONS}
          optionLabels={URGENCY_LABELS}
          selected={filters.urgencies}
          onChange={(v) => set("urgencies", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Category"
          options={filterOptions.categories}
          selected={filters.categories}
          onChange={(v) => set("categories", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Sub-Category"
          options={filterOptions.subcategories}
          selected={filters.subcategories}
          onChange={(v) => set("subcategories", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Tertiary"
          options={filterOptions.tertiaries}
          selected={filters.tertiaries}
          onChange={(v) => set("tertiaries", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Location"
          options={filterOptions.locations}
          selected={filters.locations}
          onChange={(v) => set("locations", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Market"
          options={filterOptions.markets}
          selected={filters.markets}
          onChange={(v) => set("markets", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Region"
          options={filterOptions.regions}
          selected={filters.regions}
          onChange={(v) => set("regions", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Assigned To"
          options={assignableUsers}
          selected={filters.assignees}
          onChange={(v) => set("assignees", v)}
          isDark={isDark}
        />
        <MultiSelectDropdown
          label="Manager"
          options={managerOptions.map((m) => m.email)}
          optionLabels={Object.fromEntries(managerOptions.map((m) => [m.email, m.label]))}
          selected={filters.managers}
          onChange={(v) => set("managers", v)}
          isDark={isDark}
        />

        {activeCount > 0 && (
          <Pressable style={s.clearFiltersBtn} onPress={clearAll}>
            <MaterialIcons name="filter-alt-off" size={14} color="#dc2626" />
            <Text style={s.clearFiltersText}>Clear filters</Text>
          </Pressable>
        )}
      </ScrollView>

      {/* Active filter chips summary */}
      {activeCount > 0 && (
        <View style={s.chipRow}>
          {Object.entries(filters).flatMap(([key, vals]) =>
            (vals as string[]).map((v) => {
              const displayVal =
                key === "statuses"
                  ? (STATUS_OPTIONS.find((o) => o.key === v)?.label ?? v)
                  : key === "urgencies"
                  ? (URGENCY_LABELS[v] ?? v)
                  : key === "managers"
                  ? (managerOptions.find((m) => m.email === v)?.label ?? v)
                  : v;
              return (
                <Pressable
                  key={`${key}-${v}`}
                  style={[s.chip, isDark && { backgroundColor: "#1a2f4a", borderColor: "#3D8BFF" }]}
                  onPress={() => set(key as keyof KanbanFilters, (filters[key as keyof KanbanFilters] as string[]).filter((x) => x !== v) as any)}
                >
                  <Text style={[s.chipText, isDark && { color: "#3D8BFF" }]}>{displayVal}</Text>
                  <MaterialIcons name="close" size={11} color="#3D8BFF" />
                </Pressable>
              );
            })
          )}
        </View>
      )}
    </View>
  );
};

export default SustainQueueFilterBar;

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginBottom: 16,
    overflow: "visible" as any,
    ...(Platform.OS === "web"
      ? ({
          position: "sticky",
          top: 0,
          zIndex: 100,
        } as any)
      : {}),
  },
  searchToolbar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  searchWrap: {
    flex: 1,
    minWidth: 200,
    maxWidth: 380,
    position: "relative" as any,
  },
  searchIcon: { position: "absolute" as any, left: 10, top: 11, zIndex: 2 },
  searchInput: {
    width: "100%",
    height: 36,
    borderWidth: 1,
    borderColor: "#d0d0d0",
    borderRadius: 6,
    paddingLeft: 34,
    paddingRight: 30,
    fontSize: 13,
    color: "#2d2d2d",
    outlineWidth: 0,
  } as any,
  searchClearBtn: { position: "absolute" as any, right: 10, top: 10 },
  resultCount: { marginLeft: "auto" as any, fontSize: 12, color: "#656565", fontWeight: "600" as any },
  divider: {
    height: 1,
    backgroundColor: "#f3f4f6",
    marginHorizontal: 0,
  },
  filtersRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
  },
  // ── Dropdown ──────────────────────────────────────────────────────────────
  dropWrap: {
    position: "relative" as any,
    zIndex: 10,
  },
  dropTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    minWidth: 100,
    maxWidth: 180,
  },
  dropTriggerActive: {
    borderColor: "#93C5FD",
    backgroundColor: "#EBF3FF",
  },
  dropTriggerText: {
    flex: 1,
    fontSize: 12,
    color: "#374151",
    fontWeight: "500",
  },
  dropTriggerTextActive: {
    color: "#005CBC",
    fontWeight: "600",
  },
  dropMenu: {
    minWidth: 200,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
    zIndex: 9999,
    overflow: "hidden" as any,
  },
  dropItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  dropItemActive: {
    backgroundColor: "#F0F7FF",
  },
  dropItemActiveDark: {
    backgroundColor: "#1a2f4a",
  },
  dropItemPressed: {
    backgroundColor: "#e8f0fb",
  },
  dropItemText: {
    fontSize: 13,
    color: "#374151",
  },
  dropItemTextActive: {
    color: "#005CBC",
    fontWeight: "600",
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#d1d5db",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  checkboxActive: {
    backgroundColor: "#005CBC",
    borderColor: "#005CBC",
  },
  clearAllBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: "#f3f4f6",
    alignItems: "center",
  },
  clearAllText: {
    fontSize: 12,
    color: "#6b7280",
    fontWeight: "500",
  },
  // ── Clear filters ─────────────────────────────────────────────────────────
  clearFiltersBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#FCA5A5",
    backgroundColor: "#FEF2F2",
    marginLeft: 4,
  },
  clearFiltersText: {
    fontSize: 12,
    color: "#dc2626",
    fontWeight: "600",
  },
  // ── Active filter chips ───────────────────────────────────────────────────
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 100,
    backgroundColor: "#EBF3FF",
    borderWidth: 1,
    borderColor: "#93C5FD",
  },
  chipText: {
    fontSize: 11,
    color: "#005CBC",
    fontWeight: "600",
  },
});
